import * as React from "react";
import { cn } from "@/lib/utils";
import { FaSpinner } from "react-icons/fa";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: "primary" | "secondary" | "outline" | "ghost" | "danger";
	size?: "sm" | "md" | "lg" | "xl";
	loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant = "primary", size = "md", loading, disabled, children, ...props }, ref) => {
		const variants = {
			primary: "bg-primary text-white hover:bg-primary-dark",
			secondary: "bg-gray-600 text-white hover:bg-gray-700",
			outline:
				"border border-gray-300 bg-transparent hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800",
			ghost: "bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800",
			danger: "bg-danger text-white hover:bg-danger-dark",
		};

		const sizes = {
			sm: "h-8 px-3 text-sm",
			md: "h-10 px-4 py-2",
			lg: "h-12 px-6 text-lg",
			xl: "min-h-[48px] h-14 py-3 px-6 text-base sm:text-lg",
		};

		return (
			<button
				{...props}
				className={cn(
					"inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50",
					variants[variant],
					sizes[size],
					className,
				)}
				ref={ref}
				disabled={disabled || loading}
				aria-busy={loading}
			>
				{loading && (
					<FaSpinner className="size-5 shrink-0 animate-spin" aria-hidden />
				)}
				{children}
			</button>
		);
	},
);
Button.displayName = "Button";

export { Button };
